import axios from 'axios';
import TenantService from '@/services/TenantService';
import UserService from '@/services/UserService';
import CashRegisterService from '@/services/CashRegisterService';
import Device from '@/wrappers/Device';
import router from '@/router/router';

import Store from '@/store';
import * as mutationTypes from '@/store/mutationTypes';

const ApiService = {
    defaultBaseURL: null,
    baseURL: null,
    http: null,
    interceptor401: null,
    interceptor402: null,

    /**
     * Initialize API Service
     */
    async init(baseURL) {
        this.defaultBaseURL = baseURL;
        this.baseURL = baseURL;

        this.http = axios.create({
            baseURL: baseURL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        })

        if (TenantService.getToken()) {
            ApiService.setTenantHeader();
        }

        if (UserService.getToken()) {
            ApiService.setUserHeader();
        }

        this.http.interceptors.response.use(
            response => {
                return response;
            },

            error => {
                const isApiRequest = error.config &&
                    error.config.url &&
                    !error.config.url.includes('http://') &&
                    !error.config.url.includes('http://')

                const isOfflineResponse = (!error.response && !!error.request) ||
                    error.message === 'Network Error' ||
                    error.response.status === 503;

                if (isApiRequest && isOfflineResponse) {
                    Store.commit(mutationTypes.SET_IS_ONLINE, { isOnline: false });
                }

                return Promise.reject(error);
            }
        );
    },

    setBaseUrl(baseURL) {
        this.baseURL = baseURL
        this.http.defaults.baseURL = this.baseURL
    },

    resetBaseUrl() {
        this.baseURL = this.defaultBaseURL
        this.http.defaults.baseURL = this.baseURL
    },

    /**
     * Set Tenant Authorization Header
     */
    setTenantHeader() {
        this.http.defaults.headers.common['TnAuthorization'] = `Tenant ${TenantService.getToken()}`;
    },

    /**
     * Set User Authorization Header
     */
    setUserHeader() {
        this.http.defaults.headers.common['Authorization'] = `Bearer ${UserService.getToken()}`;
    },

    /**
     * Set Cash Register Header
     */
    setCashRegisterHeader() {
        this.http.defaults.headers.common['CashRegister'] = CashRegisterService.getId();
    },

    /**
     * Set Platform Header
     */
    setDevicePlatformHeader() {
        const device = Device.get();
        this.http.defaults.headers.common['Platform'] = device ? device.platform : '';
    },

    /**
     * Set Device Uuid Header
     */
    setDeviceUuidHeader() {
        const device = Device.get();
        this.http.defaults.headers.common['Device'] = device ? device.uuid : ''
    },

    /**
     * Set Echo Socket Id Header
     */
    setSocketIdHeader(socketId) {
        this.http.defaults.headers.common['X-Socket-ID'] = socketId;
    },

    /**
     * Remove Tenant Authorization Header
     */
    removeTenantHeader() {
        delete this.http.defaults.headers.common['TnAuthorization'];
    },

    /**
     * Remove User Authorization Header
     */
    removeUserHeader() {
        delete this.http.defaults.headers.common['Authorization'];
    },

    /**
     * Remove Cash Register Header
     */
    removeCashRegisterHeader() {
        delete this.http.defaults.headers.common['CashRegister'];
    },

    /**
     * Remove Platform Header
     */
    removeDevicePlatformHeader() {
        delete this.http.defaults.headers.common['Platform'];
    },

    /**
     * Remove Echo Socket Id Header
     */
    removeSocketIdHeader() {
        delete this.http.defaults.headers.common['X-Socket-ID'];
    },

    /**
     * Handle the 401 error response
     */
    mountInterceptor401() {
        this.interceptor401 = this.http.interceptors.response.use(
            response => {
                return response;
            },

            error => {
                if (error.request && error.request.status == 401) {
                    UserService.removeToken();
                    UserService.removeUser();

                    if (error.response.data.message == 'tenant_unauthorized') {
                        TenantService.removeToken();
                        TenantService.removeTenant();
                        router.push({ name: 'tenantLogin' }).catch(err => {});
                    } else {
                        router.push({ name: 'userLogin' }).catch(err => {});
                    }
                }
                return Promise.reject(error);
            }
        );
    },

    /**
     * Handle the 402 error response
     */
    mountInterceptor402() {
        this.interceptor402 = this.http.interceptors.response.use(
            response => {
                return response;
            },

            error => {
                if (error.request && error.request.status === 402) {
                    EventBus.$emit('subscription-expired');
                }

                return Promise.reject(error)
            }
        );
    },

    /**
     * Remove the 401 interceptor
     */
    unmountInterceptor401() {
        if(this.interceptor401) {
            this.http.interceptors.response.eject(this.interceptor401);
        }
    },

    /**
     * Remove the 402 interceptor
     */
    unmountInterceptor402() {
        if(this.interceptor402) {
            this.http.interceptors.response.eject(this.interceptor402);
        }
    },

    /**
     * Get Method Wrapper
     */
    get(uri, config = null) {
        return this.http.get(uri, config);
    },

    /**
     * Post Method Wrapper
     */
    post(uri, data, config = null) {
        if (data instanceof CodForm) {
            return data.send('post', uri, config)
        }

        if (data instanceof Form) {
            return data.send('post', uri, config)
        }

        return this.http.post(uri, data, config);
    },

    /**
     * Put Method Wrapper
     */
    put(uri, data, config = null) {
        if (data instanceof CodForm) {
            return data.send('put', uri, config)
        }

        if (data instanceof Form) {
            return data.send('put', uri, config)
        }

        return this.http.put(uri, data, config);
    },

    /**
     * Patch Method Wrapper
     */
    patch(uri, data, config = null) {
        if (data instanceof CodForm) {
            return data.send('patch', uri, config)
        }

        if (data instanceof Form) {
            return data.send('patch', uri, config)
        }

        return this.http.patch(uri, data, config);
    },

    /**
     * Delete Method Wrapper
     */
    delete(uri, config = null) {
        return this.http.delete(uri, config);
    },

    /**
     * Perform a custom http request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
     **/
    customRequest(data) {
        return this.http(data);
    },
};

export default ApiService;
